import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import {
  checkForErrors,
  getPypeConnections,
  toSolutionEnvironment,
  type PlatformConnection,
} from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

/**
 * (Re)binds a channel's pype (per env) to a Live Agent Platform connection.
 * `addPypeToPypeConnection` is an idempotent upsert that does NOT detach the
 * pype from other connections, so a single-select switch removes it from the
 * previous connection first, then adds it to the new one. Either side may be
 * absent (initial bind / unbind) — mirrors `assignPypeRuleGroup`.
 *
 * The backend writes `agent_routing` / `agent_routing_config` /
 * `agent_routing_url` onto the pype for both calls; the channel form no longer
 * copies the connection's config onto itself.
 */
export const assignPypeConnection = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{
  pypeConnections: PlatformConnection[];
  cb?: () => void;
} | void> => {
  if (event.type !== 'assignPypeConnection') {
    return;
  }

  const { pypeId, env, fromConnectionId, toConnectionId, cb, onError } =
    event.payload;

  // The whole body is inside the try: `onError` has to fire for EVERY way this
  // invoke can fail, the final re-read included. That one runs after the
  // mutations already landed, so a failure there with `onError` outside the try
  // left a caller that waits on the outcome waiting forever.
  try {
    const projectId = ctx.project?.id;

    if (!projectId) {
      throwErrorWithId('Project ID is required');
      return;
    }

    const solutionEnv = toSolutionEnvironment(env);

    if (fromConnectionId) {
      const removeResponse = await urqlGql.removePypeFromPypeConnection({
        id: fromConnectionId,
        pypeId,
        env: solutionEnv,
      });
      checkForErrors(removeResponse);
    }

    if (toConnectionId) {
      const addResponse = await urqlGql.addPypeToPypeConnection({
        id: toConnectionId,
        pypeId,
        env: solutionEnv,
      });
      checkForErrors(addResponse);
    }

    const pypeConnections = await getPypeConnections({ projectId });

    return { pypeConnections, cb };
  } catch (error) {
    // The machine's error path only toasts; a caller that shows its own state
    // (the Channels not connected rows) needs to know THIS save failed. The
    // error is rethrown, so the machine's handling is unchanged.
    onError?.();
    throw error;
  }
};
